/**
 * FERP Signature Capture Component (DRR-079)
 *
 * Mantine modal wrapping signature_pad. Exports PNG data URL.
 * Sends captured signature to backend signDocument mutation.
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - Backend Snowflake IDs only — no frontend ID generation
 * - Immutable audit trail — signing creates new document version
 * - useAuth() for signer identity
 */

import { useRef, useCallback, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
} from '@mantine/core';
import { IconSignature, IconEraser, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import SignaturePad from 'signature_pad';
import { useAuth } from '@/auth';

interface SignatureCaptureProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Callback with signature PNG data URL */
  onSign: (signatureDataUrl: string, signerId: string) => void;
  /** Document name for display */
  documentName?: string;
  /** Whether signing is in progress */
  signing?: boolean;
}

export function SignatureCapture({
  opened,
  onClose,
  onSign,
  documentName,
  signing = false,
}: SignatureCaptureProps) {
  const padRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const { user } = useAuth();

  // Initialize signature pad via callback ref — fires when canvas mounts/unmounts
  const canvasCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    // Cleanup on unmount
    if (!canvas) {
      if (padRef.current) {
        padRef.current.off();
        padRef.current = null;
      }
      return;
    }

    // Initialize after a frame to ensure layout is complete
    requestAnimationFrame(() => {
      if (!canvas.isConnected) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(ratio, ratio);

      // Clean up any previous instance
      if (padRef.current) {
        padRef.current.off();
      }

      padRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 3,
      });

      padRef.current.addEventListener('endStroke', () => {
        setIsEmpty(padRef.current?.isEmpty() ?? true);
      });

      // Clear to apply background color
      padRef.current.clear();
      setIsEmpty(true);
    });
  }, []);

  const handleClear = useCallback(() => {
    padRef.current?.clear();
    setIsEmpty(true);
  }, []);

  const handleSign = useCallback(() => {
    if (!padRef.current || padRef.current.isEmpty()) return;
    const dataUrl = padRef.current.toDataURL('image/png');
    const signerId = user?.id ?? 'unknown';
    onSign(dataUrl, signerId);
  }, [onSign, user?.id]);

  const handleClose = useCallback(() => {
    if (padRef.current) {
      padRef.current.off();
      padRef.current = null;
    }
    setIsEmpty(true);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconSignature size={20} />
          <Text fw={600}>Sign Document</Text>
        </Group>
      }
      size="lg"
      centered
      zIndex={300}
      keepMounted={false}
    >
      <Stack gap="md">
        {documentName && (
          <Text size="sm" c="dimmed">
            Signing: {documentName}
          </Text>
        )}

        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          variant="light"
        >
          <Text size="sm">
            This signature will be cryptographically bound to the document and
            recorded in the immutable audit trail. This action cannot be undone.
          </Text>
        </Alert>

        <div
          style={{
            border: '2px solid var(--mantine-color-gray-4)',
            borderRadius: 4,
            backgroundColor: '#fff',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <canvas
            ref={canvasCallback}
            style={{
              width: '100%',
              height: 200,
              display: 'block',
              touchAction: 'none',
              cursor: 'crosshair',
            }}
          />
        </div>

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconEraser size={16} />}
            onClick={handleClear}
            disabled={isEmpty}
          >
            Clear
          </Button>
          <Group gap="xs">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleSign}
              disabled={isEmpty}
              loading={signing}
            >
              Apply Signature
            </Button>
          </Group>
        </Group>

        <Text size="xs" c="dimmed" ta="center">
          Signed by: {user?.id ?? 'Unknown'} | Timestamp will be recorded server-side
        </Text>
      </Stack>
    </Modal>
  );
}
